import {
  useState,
  useEffect,
  useCallback,
  useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress,
  Tooltip,
  Alert,
  TextField
} from "@mui/material";
import ChevronLeftIcon  from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon        from "@mui/icons-material/Today";
import DeleteIcon       from "@mui/icons-material/Delete";
import AddIcon          from "@mui/icons-material/Add";
import SidebarLayout    from "../components/sidebarLayout";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import Autocomplete from '../components/SearchableAutocomplete';
import { getAllPrenotazioni, eliminaPrenotazione, creaPrenotazione } from "../APIs/ReservationsAPI";
import { getAllStanze }  from "../APIs/RoomsAPI";
import { getAllClienti } from "../APIs/ClientsAPI";
import { getAllTavoli }  from "../APIs/TablesAPI";
import { SetupWSEventListener } from "../utils/setupWSEventListener";
import { getTenantTranslation } from "../utils/getTenantTranslation";
import DateTimeLocal24Field from "../components/DateTimeLocal24Field";
import { localizedErrorMessage } from "../utils/italianErrorMessage";

const HOUR_START = 7;
const HOUR_END   = 23;
const HOURS      = Array.from({ length: HOUR_END - HOUR_START }, (_, i) => HOUR_START + i);
const SLOT_H     = 60;
const localeMap  = { it: "it-IT", en: "en-US", sq: "sq-AL" };

function toMinutes(date) {
  return date.getUTCHours() * 60 + date.getUTCMinutes();
}

function fmt(date, locale) {
  return date.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function fmtTime(date, locale) {
  return date.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "UTC" });
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

const COLORS = ["#1a6b8a","#2e7d32","#6a1b9a","#c62828","#e65100","#00695c","#283593","#558b2f"];

export default function PlanningPrenotazioni() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const tenantType = localStorage.getItem("tenantType");
  const tp         = (key) => getTenantTranslation(t, tenantType, key);
  const uiLocale = useMemo(() => {
    const lng = (i18n.resolvedLanguage || i18n.language || "it").slice(0, 2).toLowerCase();
    return localeMap[lng] || "it-IT";
  }, [i18n.language, i18n.resolvedLanguage]);

  const [currentDate,  setCurrentDate]  = useState(new Date());
  const [rooms,        setRooms]        = useState([]);
  const [tables,       setTables]       = useState([]);
  const [reservations, setReservations] = useState([]);
  const [clients,      setClients]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting,     setDeleting]     = useState(false);
  const [detailTarget, setDetailTarget] = useState(null);

  const [createOpen,   setCreateOpen]   = useState(false);
  const [createSlot,   setCreateSlot]   = useState(null);
  const [createClient, setCreateClient] = useState(null);
  const [createGuests, setCreateGuests] = useState(1);
  const [createNote,   setCreateNote]   = useState("");
  const [createStart,  setCreateStart]  = useState("");
  const [createEnd,    setCreateEnd]    = useState("");
  const [creating,     setCreating]     = useState(false);
  const [createError,  setCreateError]  = useState("");

  const token   = localStorage.getItem("accessToken");
  const tenant  = localStorage.getItem("tenantId");
  const dateStr = currentDate.toISOString().split("T")[0];

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [roomData, tableData, resData, clientData] = await Promise.all([
        getAllStanze(token, tenant),
        getAllTavoli(token, tenant),
        getAllPrenotazioni(token, tenant),
        getAllClienti(token, tenant),
      ]);
      setRooms(Array.isArray(roomData)    ? roomData   : []);
      setTables(Array.isArray(tableData)  ? tableData  : []);
      setReservations(Array.isArray(resData)   ? resData   : []);
      setClients(Array.isArray(clientData) ? clientData : []);
    } catch (e) {
      setError(localizedErrorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [token, tenant]);

  useEffect(() => {
    fetchAll();
    const cleanup = SetupWSEventListener(fetchAll);
    return () => cleanup();
  }, [fetchAll]);

  const dayReservations = reservations.filter(res => {
    const d = new Date(res.startDate);
    return (
      d.getFullYear() === currentDate.getFullYear() &&
      d.getMonth()    === currentDate.getMonth()    &&
      d.getDate()     === currentDate.getDate()
    );
  });

  const tableRoomMap = {};
  tables.forEach(t => { tableRoomMap[t.id] = t.roomId; });

  const isTodayView = useMemo(() => {
    const now = new Date();
    return (
      now.getFullYear() === currentDate.getFullYear() &&
      now.getMonth() === currentDate.getMonth() &&
      now.getDate() === currentDate.getDate()
    );
  }, [currentDate]);

  const nowTopPx = useMemo(() => {
    if (!isTodayView) return null;
    const now = new Date();
    const mins = now.getHours() * 60 + now.getMinutes();
    const start = HOUR_START * 60;
    const end = HOUR_END * 60;
    if (mins < start || mins > end) return null;
    return ((mins - start) / 60) * SLOT_H;
  }, [isTodayView]);

  function getRoomReservations(roomId) {
    return dayReservations.filter(res =>
      res.tableReservations?.some(tr => tableRoomMap[tr.tableId] === roomId)
    );
  }

  function getFirstTableOfRoom(roomId) {
    return tables.find(t => t.roomId === roomId);
  }

  function getBlockStyle(res, colorIdx) {
    const start  = new Date(res.startDate);
    const end    = new Date(res.endDate);
    const startM = toMinutes(start);
    const endM   = toMinutes(end);
    const topMin = startM - HOUR_START * 60;
    const durMin = Math.max(endM - startM, 30);
    return {
      position:     "absolute",
      top:          `${(topMin / 60) * SLOT_H}px`,
      height:       `${(durMin / 60) * SLOT_H - 4}px`,
      left:         "4px",
      right:        "4px",
      background:   COLORS[colorIdx % COLORS.length],
      borderRadius: "6px",
      color:        "#fff",
      padding:      "4px 8px",
      fontSize:     "11px",
      cursor:       "pointer",
      overflow:     "hidden",
      zIndex:       2,
      boxShadow:    "0 1px 4px rgba(0,0,0,0.25)",
    };
  }

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await eliminaPrenotazione(deleteTarget.id, token, tenant);
      setDeleteTarget(null);
      setDetailTarget(null);
      fetchAll();
    } catch (e) {
      setError(localizedErrorMessage(e));
    } finally {
      setDeleting(false);
    }
  };

  const openCreate = (roomId, hour) => {
    const pad = (n) => String(n).padStart(2, "0");
    setCreateSlot({ roomId, hour });
    setCreateStart(`${dateStr}T${pad(hour)}:00`);
    setCreateEnd(`${dateStr}T${pad(hour + 1)}:00`);
    setCreateClient(null);
    setCreateGuests(1);
    setCreateNote("");
    setCreateError("");
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    if (!createClient)              { setCreateError(tp("planning.error_client"));   return; }
    if (!createStart || !createEnd) { setCreateError(tp("planning.error_dates"));    return; }
    const table = getFirstTableOfRoom(createSlot.roomId);
    if (!table)                     { setCreateError(tp("planning.error_no_table")); return; }
    setCreating(true);
    setCreateError("");
    try {
      await creaPrenotazione({
        clientId:  createClient.id,
        tableIds:  [table.id],
        guests:    createGuests,
        note:      createNote,
        startTime: new Date(createStart).toISOString(), // ✅ startTime + ISO
        endTime:   new Date(createEnd).toISOString(),   // ✅ endTime + ISO
      }, token, tenant);
      setCreateOpen(false);
      fetchAll();
    } catch (e) {
      setCreateError(localizedErrorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <SidebarLayout className="admin-responsive-page">
      <Box sx={{ mx: { xs: 1, md: 3 }, mt: 2, mb: 4, minWidth: 0 }}>

        {/* ── Toolbar ── */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>
            {tp("planning.title")}
          </Typography>
          <IconButton onClick={() => setCurrentDate(d => addDays(d, -1))}>
            <ChevronLeftIcon />
          </IconButton>
          <Button
            variant="outlined" size="small" startIcon={<TodayIcon />}
            onClick={() => setCurrentDate(new Date())}
          >
            {tp("planning.today")}
          </Button>
          <IconButton onClick={() => setCurrentDate(d => addDays(d, 1))}>
            <ChevronRightIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 500, textTransform: "capitalize" }}>
            {fmt(currentDate, uiLocale)}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {tp("planning.help")}
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Chip
            size="small"
            sx={{ bgcolor: alpha(theme.palette.success.main, 0.15), color: theme.palette.success.dark }}
            label={tp("planning.legend_available")}
          />
          <Chip
            size="small"
            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.dark }}
            label={tp("planning.legend_reserved")}
          />
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : rooms.length === 0 ? (
          <Typography color="text.secondary">{tp("planning.no_rooms")}</Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{ display: "flex", minWidth: `${60 + rooms.length * 220}px` }}>

              {/* ── Hour axis ── */}
              <Box sx={{ width: 56, flexShrink: 0, mt: "68px", position: "sticky", left: 0, zIndex: 4, bgcolor: "background.default" }}>
                {HOURS.map(h => (
                  <Box key={h} sx={{ height: `${SLOT_H}px`, display: "flex", alignItems: "flex-start", pt: "4px" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
                      {String(h).padStart(2, "0")}:00
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* ── Room columns ── */}
              {rooms.map((room, ri) => {
                const roomRes = getRoomReservations(room.id);
                const roomTables = tables.filter((tb) => tb.roomId === room.id).length;
                return (
                  <Box key={room.id} sx={{ flex: 1, minWidth: 200, mr: 1 }}>
                    <Box sx={{
                      height: "68px", display: "flex", flexDirection: "column", alignItems: "center",
                      justifyContent: "center", bgcolor: COLORS[ri % COLORS.length],
                      borderRadius: "8px 8px 0 0", px: 2, position: "sticky", top: 0, zIndex: 4,
                    }}>
                      <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
                        {room.name}
                      </Typography>
                      <Typography sx={{ color: alpha("#fff", 0.9), fontSize: 11 }}>
                        {tp("planning.room_summary", { count: roomTables })}
                      </Typography>
                    </Box>

                    <Box sx={{
                      position:     "relative",
                      height:       `${HOURS.length * SLOT_H}px`,
                      border:       "1px solid",
                      borderColor:  "divider",
                      borderTop:    "none",
                      borderRadius: "0 0 8px 8px",
                      bgcolor:      "background.paper",
                    }}>
                      {HOURS.map(h => (
                        <Box
                          key={h}
                          onClick={() => openCreate(room.id, h)}
                          sx={{
                            position:    "absolute",
                            top:         `${(h - HOUR_START) * SLOT_H}px`,
                            left: 0, right: 0,
                            height:      `${SLOT_H}px`,
                            borderTop:   "1px dashed",
                            borderColor: alpha(theme.palette.divider, 0.7),
                            cursor:      "pointer",
                            bgcolor: h % 2 === 0 ? alpha(theme.palette.success.main, 0.04) : "transparent",
                            "&:hover":   { bgcolor: alpha(theme.palette.success.main, 0.12) },
                            zIndex:      1,
                          }}
                        >
                          <Tooltip title={`${tp("planning.add_at")} ${String(h).padStart(2,"0")}:00`} placement="left">
                            <Box sx={{
                              position:  "absolute", right: 6, top: "50%",
                              transform: "translateY(-50%)", opacity: 0.6,
                              ".MuiBox-root:hover > &": { opacity: 1 },
                            }}>
                              <AddIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                            </Box>
                          </Tooltip>
                        </Box>
                      ))}

                      {nowTopPx !== null && (
                        <Box
                          sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            top: `${nowTopPx}px`,
                            borderTop: `2px solid ${theme.palette.error.main}`,
                            zIndex: 3,
                          }}
                        />
                      )}

                      {roomRes.map((res) => (
                        <Box
                          key={res.id}
                          sx={getBlockStyle(res, ri)}
                          onClick={e => { e.stopPropagation(); setDetailTarget(res); }}
                        >
                          <Typography sx={{ fontWeight: 700, fontSize: 11, lineHeight: 1.2 }}>
                            {res.client?.name} {res.client?.surname}
                          </Typography>
                          <Typography sx={{ fontSize: 10, opacity: 0.85 }}>
                            {fmtTime(new Date(res.startDate), uiLocale)} – {fmtTime(new Date(res.endDate), uiLocale)}
                          </Typography>
                          {res.guests && (
                            <Typography sx={{ fontSize: 10, opacity: 0.75 }}>
                              👥 {res.guests}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                    {roomRes.length === 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.6 }}>
                        {tp("planning.empty_day")}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Detail dialog ── */}
      <Dialog open={!!detailTarget} onClose={() => setDetailTarget(null)} maxWidth="xs" fullWidth>
        {detailTarget && (
          <>
            <DialogTitle>{tp("planning.reservation_detail")}</DialogTitle>
            <DialogContent>
              <Typography variant="body2" gutterBottom>
                <strong>{t("client")}:</strong> {detailTarget.client?.name} {detailTarget.client?.surname}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>{tp("planning.time")}:</strong> {fmtTime(new Date(detailTarget.startDate), uiLocale)} – {fmtTime(new Date(detailTarget.endDate), uiLocale)}
              </Typography>
              {detailTarget.guests && (
                <Typography variant="body2" gutterBottom>
                  <strong>{tp("planning.guests")}:</strong> {detailTarget.guests}
                </Typography>
              )}
              {detailTarget.note && (
                <Typography variant="body2" gutterBottom>
                  <strong>{tp("planning.note")}:</strong> {detailTarget.note}
                </Typography>
              )}
              {detailTarget.notes?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2"><strong>{tp("planning.notes")}:</strong></Typography>
                  {detailTarget.notes.map(n => (
                    <Chip key={n.id} label={n.note} size="small" sx={{ mt: 0.5, mr: 0.5 }} />
                  ))}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailTarget(null)}>{t("close")}</Button>
              <Button color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteTarget(detailTarget)}>
                {tp("planning.delete_reservation")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* ── Confirm delete dialog ── */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t("confirm_delete")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("confirm_delete_msg", { count: 1 })}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>{t("cancel")}</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? tp("planning.deleting") : tp("planning.delete_reservation")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create dialog ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{tp("planning.new_reservation")}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <Autocomplete
              options={clients}
              getOptionLabel={c => `${c.name} ${c.surname}${c.email ? ` — ${c.email}` : ""}`}
              value={createClient}
              onChange={(_, v) => setCreateClient(v)}
              renderInput={params => (
                <TextField {...params} label={t("client")} size="small" required />
              )}
            />
            <DateTimeLocal24Field
              label={tp("planning.start_time")}
              size="small"
              value={createStart}
              onChange={setCreateStart}
            />
            <DateTimeLocal24Field
              label={tp("planning.end_time")}
              size="small"
              value={createEnd}
              onChange={setCreateEnd}
            />
            <TextField
              label={tp("planning.guests")} size="small" type="number"
              value={createGuests} onChange={e => setCreateGuests(parseInt(e.target.value) || 1)}
              inputProps={{ min: 1 }}
            />
            <TextField
              label={tp("planning.note")} size="small" multiline rows={2}
              value={createNote} onChange={e => setCreateNote(e.target.value)}
            />
            {createError && <Alert severity="error">{createError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>{t("cancel")}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={creating}>
            {creating ? <CircularProgress size={18} /> : tp("planning.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </SidebarLayout>
  );
}
